import useGetTotalsCentAmount from '@/modules/perseo-billing/hooks/use-get-totals-cent-amount';
import { useGiftCardData } from '@/modules/perseo-billing/hooks/use-gift-card-data';
import { formatPrice } from '@/modules/perseo-billing/hooks/utils';

interface CartTotals {
  otherMethods: {
    amount: number;
    price: string;
    withGiftCard?: {
      amount: number;
      price: string;
    };
  };
  cencoPay: {
    amount: number;
    price: string;
  };
  cencosudCard: {
    amount: number;
    price: string;
  };
  giftCard?: {
    amount: number;
    price: string;
  };
  hasGiftCardApplied?: boolean;
}

const hasGiftCardAppliedPrice = (totals: CartTotals): boolean => {
  const hasGiftCard: boolean =
    (totals?.giftCard &&
      totals?.giftCard?.amount > 0 &&
      totals?.otherMethods?.withGiftCard &&
      totals?.otherMethods?.withGiftCard?.amount > 0) ??
    false;

  return hasGiftCard;
};

export const useGetCartTotals = (): CartTotals => {
  const { cencoPayAmount, cencosudAmount, otherMethodsAmount } =
    useGetTotalsCentAmount();
  const { getGiftCardAmount } = useGiftCardData();
  const giftCardAmount = getGiftCardAmount();

  const otherMethodsPrice = formatPrice(otherMethodsAmount) ?? '';
  const otherMethodsPriceAsNumber = otherMethodsAmount;

  const otherMethodWithGiftCardApplied = otherMethodsAmount - giftCardAmount;
  const otherMethodsPriceWithGiftCard =
    formatPrice(otherMethodWithGiftCardApplied) ?? '';

  let cencosudCardPrice = '';
  let cencosudCardPriceAsNumber = 0;

  let cencoPayPrice = '';
  let cencoPayPriceAsNumber = 0;

  const hasBothMethods = Boolean(cencosudAmount) && Boolean(cencoPayAmount);

  cencosudCardPrice = formatPrice(cencosudAmount);
  cencosudCardPriceAsNumber = cencosudAmount;

  cencoPayPrice = formatPrice(cencoPayAmount);
  cencoPayPriceAsNumber = cencoPayAmount;

  if (hasBothMethods && cencoPayAmount >= cencosudAmount) {
    cencosudCardPrice = formatPrice(cencosudAmount - giftCardAmount);
    cencosudCardPriceAsNumber = cencosudAmount - giftCardAmount;
  }

  if (hasBothMethods && cencoPayAmount < cencosudAmount) {
    cencoPayPrice = formatPrice(cencoPayAmount);
    cencoPayPriceAsNumber = cencoPayAmount;
    cencosudCardPriceAsNumber = cencosudAmount - giftCardAmount;
  }

  if (giftCardAmount > 0) {
    cencosudCardPrice = otherMethodsPrice;
    cencosudCardPriceAsNumber = otherMethodsPriceAsNumber;
  }

  const cartPayments: CartTotals = {
    otherMethods: {
      amount: otherMethodsAmount,
      price: otherMethodsPrice,
      withGiftCard: {
        amount: otherMethodWithGiftCardApplied,
        price: otherMethodsPriceWithGiftCard
      }
    },
    cencosudCard: {
      amount: cencosudCardPriceAsNumber,
      price: cencosudCardPrice
    },
    cencoPay: {
      amount: cencoPayPriceAsNumber,
      price: cencoPayPrice
    },
    giftCard: {
      amount: giftCardAmount,
      price: formatPrice(giftCardAmount)
    }
  };

  const hasGiftCard = hasGiftCardAppliedPrice(cartPayments);

  return {
    ...cartPayments,
    hasGiftCardApplied: hasGiftCard
  };

  return cartPayments;
};
